import React, { useState, useEffect } from 'react';
import api from '../api';
import { FaSolarPanel, FaRupeeSign, FaFileAlt, FaCheckCircle, FaStar } from 'react-icons/fa';

const PMSuryaghar = () => {
  const [data, setData] = useState(null);

  // --- ICON MAP (DB String -> React Component) ---
  const iconMap = {
    "FaSolarPanel": <FaSolarPanel />,
    "FaRupeeSign": <FaRupeeSign />,
    "FaFileAlt": <FaFileAlt />,
    "FaCheckCircle": <FaCheckCircle />
  };



  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/PMScheme');
        if (res.data) {
          setData(res.data);
        }
      } catch (err) {
        console.error("Error fetching data", err);
      }
    };
    fetchData();
  }, []);

  // Show loading until data arrives
  if (!data || !data.info) return <div className="text-center py-5">Loading Scheme Details...</div>;

  // Destructure for easier usage
  const { info, slabs, steps, netMetering, benefits } = data;

  return (
    <section className="pm-suryaghar-section py-5 bg-white" id="pm-suryaghar">
      <div className="container">
        
        {/* Section Header (Dynamic) */}
        <div className="text-center mb-5">
          <span className="badge bg-danger mb-3 px-3 py-2">
            <i className="bi bi-lightning-charge-fill me-2"></i>
            Government Scheme
          </span>
          <h2 className="display-5 fw-bold text-dark mb-3">
            {info.mainTitle}
          </h2>
          <p className="lead text-muted mb-4">
            {info.highlightText}
          </p>
          <div className="alert alert-success d-inline-block" role="alert">
            <i className="bi bi-info-circle-fill me-2"></i>
            RENGIE is an <strong>approved implementation partner</strong> under PM Suryaghar Yojana
          </div>
        </div>

        {/* About Scheme (Dynamic) */}
        <div className="row mb-5">
          <div className="col-lg-10 mx-auto">
            <div className="card border-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #E3F2FD 0%, #E8F8F3 100%)' }}>
              <div className="card-body p-4">
                <h4 className="fw-bold mb-3" style={{ color: '#1B3F6E' }}>
                  <i className="bi bi-info-circle-fill me-2" style={{ color: '#00D084' }}></i>
                  About PM Suryaghar Yojana
                </h4>
                <p className="text-muted mb-3">
                  {info.description}
                </p>
                <p className="text-muted mb-0">
                  <i className="bi bi-calendar-check me-2" style={{ color: '#00D084' }}></i>
                  <strong>{info.subtitle}</strong>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Subsidy Table - Individual Households (Dynamic Slabs) */}
        <div className="mb-5">
          <h3 className="text-center fw-bold mb-4" style={{ color: '#1B3F6E' }}>
            <i className="bi bi-house-fill me-2" style={{ color: '#00D084' }}></i>
            Subsidy for Individual Households
          </h3>
          <div className="row g-4">
            {slabs.map((item, index) => (
              <div className="col-lg-3 col-md-6" key={index}>
                <div 
                  className="card h-100 border-0 shadow-sm subsidy-card" 
                  style={{ borderTop: `4px solid ${item.colorHex}` }}
                >
                  <div className="card-body text-center p-4">
                    <div 
                      className="subsidy-icon mb-3"
                      style={{ fontSize: '2.5rem', color: item.colorHex }}
                    >
                      <i className="bi bi-house-check-fill"></i>
                    </div>
                    <h5 className="fw-bold mb-3" style={{ color: '#1B3F6E' }}>
                      {item.capacity}
                    </h5>
                    <p className="text-muted small mb-3">
                      {item.consumptionRange}
                    </p>
                    <div 
                      className="subsidy-amount py-3 px-2 rounded"
                      style={{ background: `${item.colorHex}15` }}
                    >
                      <h3 className="fw-bold mb-0" style={{ color: item.colorHex }}>
                        {item.subsidyAmount}
                      </h3>
                      <small className="text-muted">Subsidy Amount</small>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* GHS/RWA Subsidy (Dynamic from Info) */}
        <div className="mb-5">
          <h3 className="text-center fw-bold mb-4" style={{ color: '#1B3F6E' }}>
            <i className="bi bi-buildings me-2" style={{ color: '#00D084' }}></i>
            Subsidy for Housing Societies & RWAs
          </h3>
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="card border-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #1B3F6E 0%, #2E5C8A 100%)' }}>
                <div className="card-body p-5 text-white">
                  <div className="text-center mb-4">
                    <i className="bi bi-buildings" style={{ fontSize: '4rem', color: '#00D084' }}></i>
                  </div>
                  <h4 className="text-center fw-bold mb-4">For Group Housing Society (GHS) / Resident Welfare Association (RWA)</h4>
                  <div className="row text-center">
                    <div className="col-md-4 mb-3 mb-md-0">
                      <div className="p-3 bg-white bg-opacity-10 rounded">
                        <h5 className="fw-bold" style={{ color: '#00D084' }}>{info.ghS_Subsidy}</h5>
                        <small>Subsidy Rate</small>
                      </div>
                    </div>
                    <div className="col-md-4 mb-3 mb-md-0">
                      <div className="p-3 bg-white bg-opacity-10 rounded">
                        <h5 className="fw-bold" style={{ color: '#00D084' }}>{info.ghS_Capacity}</h5>
                        <small>System Capacity</small>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="p-3 bg-white bg-opacity-10 rounded">
                        <h5 className="fw-bold" style={{ color: '#00D084' }}>EV Charging</h5>
                        <small>Included</small>
                      </div>
                    </div>
                  </div>
                  <p className="text-center mt-4 mb-0 text-white-50">
                    <i className="bi bi-info-circle me-2"></i>
                    Solar for Common facilities, including EV Charging
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Net Metering Explanation (Dynamic Data from NetMeteringInfo Table) */}
        {netMetering && (
          <div className="net-metering-section mb-5">
            <h3 className="text-center fw-bold mb-4" style={{ color: '#1B3F6E' }}>
              <i className="bi bi-arrow-left-right me-2" style={{ color: '#00D084' }}></i>
              Understanding Net Metering
            </h3>
            <p className="text-center text-muted mb-5">
              Net metering allows you to export excess solar power to the grid and get credit at the same rate
            </p>
            
            <div className="row g-4 mb-4">
              {/* Case 1: Dynamic Data */}
              <div className="col-md-6">
                <div className="card border-0 shadow-lg h-100">
                  <div className="card-header text-white text-center py-3" style={{ background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)' }}>
                    <h5 className="mb-0 fw-bold">
                      <i className="bi bi-arrow-down-circle me-2"></i>
                      {netMetering.case1_Title}
                    </h5>
                  </div>
                  <div className="card-body p-4">
                    <h6 className="fw-bold mb-4 text-center" style={{ color: '#1B3F6E' }}>
                      {netMetering.case1_Desc}
                    </h6>
                    
                    <div className="text-center mb-4">
                      <div className="d-flex justify-content-center align-items-center gap-3 flex-wrap">
                        <div>
                          <div className="icon-box mb-2 mx-auto" style={{ width: '90px', height: '90px', background: 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', boxShadow: '0 8px 20px rgba(255, 215, 0, 0.3)' }}>☀️</div>
                          <strong style={{ fontSize: '1.8rem', color: '#FF9800' }}>{netMetering.case1_Solar}</strong>
                          <p className="small text-muted mb-0">Solar Generated</p>
                        </div>
                        <div><i className="bi bi-arrow-right-circle-fill" style={{ fontSize: '2.5rem', color: '#00D084' }}></i></div>
                        <div>
                          <div className="icon-box mb-2 mx-auto" style={{ width: '90px', height: '90px', background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', boxShadow: '0 8px 20px rgba(33, 150, 243, 0.3)' }}>🏠</div>
                          <strong style={{ fontSize: '1.8rem', color: '#2196F3' }}>5 kW</strong>
                          <p className="small text-muted mb-0">Home Used</p>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-3" style={{ borderTop: '2px dashed #E0E0E0' }}>
                        <i className="bi bi-plug-fill" style={{ fontSize: '2rem', color: '#F44336' }}></i>
                        <p className="fw-bold mt-2 mb-1" style={{ color: '#F44336', fontSize: '1.2rem' }}>
                          {netMetering.case1_Grid}
                        </p>
                        <small className="text-muted">You pay for these units only</small>
                      </div>
                    </div>
                    
                    <div className="alert alert-warning mb-0" style={{ background: '#FFF3E0', border: 'none' }}>
                      <i className="bi bi-lightbulb-fill me-2" style={{ color: '#FF9800' }}></i>
                      <small>When consumption exceeds solar generation, you import the remaining power from the grid.</small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Case 2: Dynamic Data */}
              <div className="col-md-6">
                <div className="card border-0 shadow-lg h-100">
                  <div className="card-header text-white text-center py-3" style={{ background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)' }}>
                    <h5 className="mb-0 fw-bold">
                      <i className="bi bi-arrow-up-circle me-2"></i>
                      {netMetering.case2_Title}
                    </h5>
                  </div>
                  <div className="card-body p-4">
                    <h6 className="fw-bold mb-4 text-center" style={{ color: '#1B3F6E' }}>
                      {netMetering.case2_Desc}
                    </h6>
                    
                    <div className="text-center mb-4">
                      <div className="d-flex justify-content-center align-items-center gap-3 flex-wrap">
                        <div>
                          <div className="icon-box mb-2 mx-auto" style={{ width: '90px', height: '90px', background: 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', boxShadow: '0 8px 20px rgba(255, 215, 0, 0.3)' }}>☀️</div>
                          <strong style={{ fontSize: '1.8rem', color: '#FF9800' }}>{netMetering.case2_Solar}</strong>
                          <p className="small text-muted mb-0">Solar Generated</p>
                        </div>
                        <div><i className="bi bi-arrow-right-circle-fill" style={{ fontSize: '2.5rem', color: '#00D084' }}></i></div>
                        <div>
                          <div className="icon-box mb-2 mx-auto" style={{ width: '90px', height: '90px', background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', boxShadow: '0 8px 20px rgba(33, 150, 243, 0.3)' }}>🏠</div>
                          <strong style={{ fontSize: '1.8rem', color: '#2196F3' }}>2 kW</strong>
                          <p className="small text-muted mb-0">Home Used</p>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-3" style={{ borderTop: '2px dashed #E0E0E0' }}>
                        <i className="bi bi-lightning-charge-fill" style={{ fontSize: '2rem', color: '#4CAF50' }}></i>
                        <p className="fw-bold mt-2 mb-1" style={{ color: '#4CAF50', fontSize: '1.2rem' }}>
                          {netMetering.case2_Grid}
                        </p>
                        <small className="text-muted">You get credit at same rate</small>
                      </div>
                    </div>
                    
                    <div className="alert alert-success mb-0" style={{ background: '#E8F5E9', border: 'none' }}>
                      <i className="bi bi-cash-coin me-2" style={{ color: '#4CAF50' }}></i>
                      <small>Excess power exported to grid earns you credit, reducing your next month's bill.</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-lg-10 mx-auto">
                <div className="alert d-flex align-items-center" style={{ background: 'linear-gradient(135deg, #E3F2FD 0%, #E8F8F3 100%)', border: '2px solid #00D084', borderRadius: '12px' }}>
                  <div className="me-3"><i className="bi bi-info-circle-fill" style={{ fontSize: '2.5rem', color: '#00D084' }}></i></div>
                  <div>
                    <h6 className="fw-bold mb-2" style={{ color: '#1B3F6E' }}>Monthly Net Adjustment</h6>
                    <p className="mb-0 text-muted small">{netMetering.footerText}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* How We Help (Dynamic Steps) */}
        <div className="mb-5">
          <h3 className="text-center fw-bold mb-5" style={{ color: '#1B3F6E' }}>
            <i className="bi bi-hand-thumbs-up-fill me-2" style={{ color: '#00D084' }}></i>
            How RENGIE Helps You Get Maximum Subsidy
          </h3>
          <div className="row g-4">
            {steps.map((step, index) => (
              <div className="col-lg-3 col-md-6" key={index}>
                <div className="card h-100 border-0 shadow-sm text-center help-card">
                  <div className="card-body p-4">
                    <div className="step-number mb-3">
                      <span className="badge rounded-circle p-3" style={{ fontSize: '1.2rem', background: 'linear-gradient(135deg, #00D084 0%, #00A86B 100%)', width: '50px', height: '50px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        {step.stepNumber}
                      </span>
                    </div>
                    <div className="help-icon mb-3" style={{ fontSize: '2.5rem', color: '#00D084' }}>
                      {iconMap[step.iconName] || <FaCheckCircle />}
                    </div>
                    <h5 className="fw-bold mb-3" style={{ color: '#1B3F6E' }}>
                      {step.title}
                    </h5>
                    <p className="text-muted small mb-0">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits List (Dynamic - Filtered by Category) */}
        <div className="row mb-5">
          <div className="col-lg-10 mx-auto">
            <div className="card border-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #E8F8F3 0%, #E3F2FD 100%)' }}>
              <div className="card-body p-5">
                <h4 className="fw-bold mb-4 text-center" style={{ color: '#1B3F6E' }}>
                  <i className="bi bi-star-fill me-2" style={{ color: '#FFD700' }}></i>
                  Our Structured Approach Ensures
                </h4>
                <div className="row">
                  <div className="col-md-6">
                    <ul className="list-unstyled">
                      {benefits.filter(b => b.category === 'Left').map((b, i) => (
                        <li key={i} className="mb-3">
                          <i className="bi bi-check-circle-fill text-success fs-5 me-3"></i>
                          <span className="fw-semibold">{b.benefitText}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <ul className="list-unstyled">
                      {benefits.filter(b => b.category === 'Right').map((b, i) => (
                        <li key={i} className="mb-3">
                          <i className="bi bi-check-circle-fill text-success fs-5 me-3"></i>
                          <span className="fw-semibold">{b.benefitText}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <a href="#contact" className="btn btn-lg rounded-pill px-5 py-3 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #00D084 0%, #00A86B 100%)', color: '#ffffff', border: 'none', fontSize: '1.1rem', transition: 'all 0.3s ease' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 15px 40px rgba(0, 208, 132, 0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)'; }}
          >
            <i className="bi bi-calculator me-2"></i> Check Your Eligibility & Get Free Quote
          </a>
          <p className="text-muted small mt-3 mb-0">
            <i className="bi bi-telephone-fill me-2"></i> Or call us: <strong>+91-9031654981</strong>
          </p>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .subsidy-card { transition: all 0.3s ease; }
        .subsidy-card:hover { transform: translateY(-10px); box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15) !important; }
        .help-card { transition: all 0.3s ease; position: relative; overflow: hidden; }
        .help-card:hover { transform: translateY(-5px); box-shadow: 0 10px 30px rgba(0, 208, 132, 0.2) !important; }
        .help-card::before { content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 4px; background: linear-gradient(90deg, #00D084, #00A86B); transform: scaleX(0); transition: transform 0.3s ease; }
        .help-card:hover::before { transform: scaleX(1); }
        .icon-box { transition: all 0.3s ease; }
        .icon-box:hover { transform: scale(1.1); }
      `}</style>
    </section>
  );
};

export default PMSuryaghar;